import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { MouseEventHandler } from 'react';
import { asyncTrackingEvent, asyncTrackingEventProps } from '../../app/utils/tracking';
import { BookmarkType, CardAttributes, CardInfo } from '../../types/cards';
import Follow from '../bookmarks/Follow';
import Save from '../bookmarks/Save';
import { siteLogo } from '../layout/utils/Logo';
import Video from '../layout/utils/Video';
import { Card } from '../ui/Card';
import { getCardLabelByType } from './Card';

interface FeedCardProps extends React.HTMLAttributes<HTMLDivElement> {
  card: CardInfo;
  attributes: CardAttributes;
}

/**
 * Card that appears for each entity on the dashboard/feed page
 *
 * @param {CardInfo} card
 * @param {CardAttributes} attributes
 * @return {JSX}
 */
const FeedCard = ({ card, attributes }: FeedCardProps) => {
  const data = card.data;

  const thumbnail =
    card.images?.thumbnail && !data.ignoreThumbnail ? (
      card.images?.thumbnail.type.toLowerCase() === 'gatsby' ? (
        <GatsbyImage
          className="w-full"
          image={card.images?.thumbnail?.image}
          alt={
            card.images?.thumbnail?.alt && card.images?.thumbnail?.alt.length > 0
              ? card.images?.thumbnail?.alt
              : `Thumbnail for ${data.title}`
          }
        />
      ) : (
        <img
          className="w-full"
          alt={
            card.images?.thumbnail?.alt && card.images?.thumbnail?.alt.length > 0
              ? card.images?.thumbnail?.alt
              : `Thumbnail for ${data.title}`
          }
          src={card.images?.thumbnail?.image}
        />
      )
    ) : null;
  const banner = card.images?.banner ? (
    card.images?.banner.type.toLowerCase() === 'gatsby' ? (
      <GatsbyImage
        className="w-full"
        image={card.images?.banner?.image}
        alt={
          card.images?.banner?.alt && card.images?.banner?.alt.length > 0
            ? card.images?.banner?.alt
            : `Banner for ${data.title}`
        }
      />
    ) : (
      <img
        className="w-full"
        alt={
          card.images?.banner?.alt && card.images?.banner?.alt.length > 0
            ? card.images?.banner?.alt
            : `Banner for ${data.title}`
        }
        src={card.images?.banner?.image}
      />
    )
  ) : null;
  const profile =
    card.type === 'graduate_profile' ? (
      card.images?.thumbnail ? (
        card.images?.thumbnail.type.toLowerCase() === 'gatsby' ? (
          <GatsbyImage
            className="h-24 w-24 md:w-[122px] md:h-[122px] rounded-full border-2 border-gray-200 shadow-sm"
            image={card.images?.thumbnail?.image}
            alt={
              card.images?.thumbnail?.alt && card.images?.thumbnail?.alt.length > 0
                ? card.images?.thumbnail?.alt
                : `Profile for ${data.title}`
            }
          />
        ) : (
          <img
            className="h-24 w-24 md:w-[122px] md:h-[122px] rounded-full border-2 border-gray-200 shadow-sm"
            alt={
              card.images?.thumbnail?.alt && card.images?.thumbnail?.alt.length > 0
                ? card.images?.thumbnail?.alt
                : `Profile for ${data.title}`
            }
            src={card.images?.thumbnail?.image}
          />
        )
      ) : null
    ) : null;
  const logo = card.images?.logo ? (
    card.images?.logo.type.toLowerCase() === 'gatsby' ? (
      <GatsbyImage
        image={card.images?.logo?.image}
        alt={
          card.images?.logo?.alt && card.images?.logo?.alt.length > 0
            ? card.images?.logo?.alt
            : `Logo for ${data.title}`
        }
      />
    ) : card.images?.logo.type.toLowerCase() === 'html' ? (
      <div className="min-h-[3.5rem] min-w-[3.5rem] flex flex-col rounded-sm overflow-hidden will-change-transform">
        {card.images?.logo?.image}
      </div>
    ) : (
      <img
        alt={
          card.images?.logo?.alt && card.images?.logo?.alt.length > 0
            ? card.images?.logo?.alt
            : `Logo for ${data.title}`
        }
        src={card.images?.logo?.image}
      />
    )
  ) : !data.ignoreDefaultLogo ? (
    siteLogo(process.env.GATSBY_SITE_NAME as string, 'h-full max-h-full p-1', true)
  ) : null;

  const mediaImage = profile ? profile : thumbnail ? thumbnail : banner;

  const mediaVideo = data.videoUrl ? (
    <div className="bg-black">
      <Video
        url={data.videoUrl}
        onPlay={() => {
          const event: asyncTrackingEventProps = {
            track: `video_play`,
            trackAbacus: `video_play`,
            trackText: 'video_click_feed',
            trackValue: 'video',
            trackId: data?.uuid,
            trackUrl: data?.videoUrl
          };
          asyncTrackingEvent(event);
        }}
        onPause={() => {
          const event: asyncTrackingEventProps = {
            track: `video_pause`,
            trackAbacus: `video_pause`,
            trackText: 'video_click_feed',
            trackValue: 'video',
            trackId: data?.uuid,
            trackUrl: data?.videoUrl
          };
          asyncTrackingEvent(event);
        }}
        className="aspect-video w-full"
      />
    </div>
  ) : null;
  const logoHTML = (
    <div className="relative">
      <div className="shadow-sm rounded-sm">
        <div className="h-14 w-14 p-1 flex justify-center items-center bg-white">{logo}</div>
      </div>
    </div>
  );
  const titleHTML = (
    <h4
      className={`font-medium leading-6 line-clamp-2 text-xl text-gray group-hover:text-primary transition duration-200 ease-in-out ${
        data.featured ? 'pr-16' : ''
      }`}
    >
      {data.title}
    </h4>
  );

  const overrideLabel = card.type === 'graduate_profile' ? card.attributes.headline : card.attributes.opportunityType;

  return (
    <Card className="active:border-gray-200 rounded-2xl border border-gray-200 bg-background dark:border-gray-200/20 transition duration-200 ease-in-out hover:bg-primary-50/50 hover:border-primary-200/50">
      <div className={`flex justify-start p-4 ${!mediaImage && !mediaVideo ? 'pb-0' : ''} ${logo ? 'space-x-4' : ''}`}>
        {logo && !data.organisationUrl && logoHTML}
        {logo && data.organisationUrl && (
          <Link data-cy={`image:view-organisation`} to={data.organisationUrl}>
            {logoHTML}
          </Link>
        )}
        <div className="flex flex-col justify-center space-y-1.5">
          {((logo && !data.organisationUrl) || !logo) && titleHTML}
          {logo && data.organisationUrl && (
            <Link data-cy={`image:view-organisation`} to={data.organisationUrl}>
              {titleHTML}
            </Link>
          )}
          {data.subTitle && (
            <h5
              className="text-gray-700 leading-6 text-sm font-medium"
              dangerouslySetInnerHTML={{ __html: data.subTitle }}
            ></h5>
          )}
          <div
            className="text-gray-700 text-xs"
            dangerouslySetInnerHTML={{
              __html: getCardLabelByType(card.type as any, true, overrideLabel) as string
            }}
          ></div>
        </div>
      </div>
      {(mediaVideo || mediaImage) && card.type !== 'graduate_profile' && (
        <div className="w-full">{mediaVideo ? mediaVideo : mediaImage}</div>
      )}
      {(mediaVideo || mediaImage) && card.type === 'graduate_profile' && (
        <div className="w-full bg-mint-400 justify-center items-center flex py-2">{mediaImage}</div>
      )}
      <div className="p-4">
        {data.descriptionTitle && typeof data.descriptionTitle === 'string' ? (
          <div
            className={`flex uppercase text-gray-700 text-xs font-semibold tracking-wide pb-2`}
            dangerouslySetInnerHTML={{ __html: data.descriptionTitle }}
          ></div>
        ) : (
          <div className={`flex uppercase text-gray-700 text-xs font-semibold tracking-wide pb-2`}>
            {data.descriptionTitle}
          </div>
        )}
        {data.description && (
          <div
            className="text-gray text-sm leading-6 line-clamp-3"
            dangerouslySetInnerHTML={{ __html: data.description }}
          ></div>
        )}
        <div className="flex justify-between items-end h-8 mt-1">
          <div className="font-normal text-gray-700 text-xs tracking-wide line-clamp-2">
            <span
              className="block pr-4"
              dangerouslySetInnerHTML={{
                __html: data.descriptionFooterTitle
              }}
            ></span>
          </div>
          <div className={`${card.bookmarkType === BookmarkType.Save ? '' : ''} -mb-0.5 flex-shrink-0`}>
            {card.id &&
              !attributes.disableActions &&
              (card.bookmarkType === BookmarkType.Save ? (
                <Save contentType={card.type as any} id={card.id} icon bookmarkBtnId={data.bookmarkBtnId} />
              ) : (
                <Follow id={card.id} bookmarkBtnId={data.bookmarkBtnId} icon />
              ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default FeedCard;
